/* DASH-CUSTOM-02 — DEFAULT LAYOUT PER RUOLO (V1)
 * ============================================================================
 * Il default di ciascun ruolo e' la BASE: la configurazione personale e' solo
 * un overlay sopra questo valore (audit §14).
 *
 * Il default NON viene mai copiato nel record dell'utente: si risolve al momento
 * dell'uso. Copiarlo significherebbe che ogni utente eredita una copia statica
 * che non segue i successivi aggiornamenti.
 *
 * D-01 (RUOLI): nella V1 esiste SOLO MANAGER.
 *   CONTABILITA e ACCOUNT restano riconosciuti da ruoloDashboard() ma NON
 *   ammessi alla Dashboard Manager. NON si inventano i loro default: manca il
 *   requisito funzionale che li descriva.
 *   La struttura e' gia' multi-ruolo: aggiungere un ruolo significa aggiungere
 *   una chiave a DEFAULTS_BY_ROLE, senza toccare il resto.
 */

import { WIDGET_REGISTRY_V1 } from './widgetRegistry';

export const DEFAULT_ROLE_MANAGER = 'MANAGER';

/**
 * Ordine LOGICO dei widget personalizzabili per il ruolo MANAGER.
 *
 * Riflette la Dashboard Manager approvata come baseline funzionale.
 * NON e' (e non deve diventare in DASH-CUSTOM-02) l'array che il template
 * renderizza: DashboardManager.vue continua a usare il proprio layout righe
 * Bootstrap. Nessuna differenza visibile.
 *
 * `InSviluppoCard` NON compare: e' un placeholder, non un widget (D-06).
 * `ManagerFilterBar` NON compare: e' infrastruttura, non un widget.
 */
export const DEFAULT_MANAGER_V1_ORDER = Object.freeze([
    'welcome',
    'kpi',
    'project_status',
    'activity',
    'trend',
    'in_sviluppo',
    'heatmap',
    'rankings',
    'supplier_spend',
    'recent',
]);

/* DASH-CUSTOM-06F - posizioni DEFAULT della griglia a 12 colonne (desktop).
 * welcome e' SYSTEM (fisso nel renderer, NON in questa mappa). Queste sono il
 * fallback del renderer per i widget senza position personale e il target del
 * RESET. Geometria: fascia1 5/7, fascia2 7/5, fascia3 12/12, fascia4 7/5. */
export const DEFAULT_MANAGER_V2_GRID_POSITIONS = Object.freeze({
    kpi:            Object.freeze({ row: 1, column: 0, rowSpan: 1 }),
    project_status: Object.freeze({ row: 0, column: 5, rowSpan: 2 }),
    activity:       Object.freeze({ row: 0, column: 9, rowSpan: 2 }),
    trend:          Object.freeze({ row: 2, column: 0, rowSpan: 1 }),
    in_sviluppo:    Object.freeze({ row: 2, column: 7, rowSpan: 1 }),
    heatmap:        Object.freeze({ row: 3, column: 0, rowSpan: 1 }),
    rankings:       Object.freeze({ row: 4, column: 0, rowSpan: 1 }),
    supplier_spend: Object.freeze({ row: 4, column: 7, rowSpan: 1 }),
    recent:         Object.freeze({ row: 5, column: 0, rowSpan: 1 }),
});

/**
 * Costruisce il default del ruolo MANAGER.
 *
 * Ogni chiave presente nell'ordine riceve `enabled: true`: nella V1 non esiste
 * un widget di default disattivato. Se in futuro un widget dovesse nascere
 * spento, il default va dichiarato qui e non dedotto dal registry.
 */
export function buildDefaultManagerV1() {
    const order = DEFAULT_MANAGER_V1_ORDER.slice();
    const widgets = {};
    order.forEach((key) => {
        widgets[key] = { enabled: true };
    });
    return {
        version: WIDGET_REGISTRY_V1.schemaVersion,
        order,
        widgets,
    };
}

export const DEFAULTS_BY_ROLE = Object.freeze({
    MANAGER: Object.freeze(buildDefaultManagerV1()),
});

/**
 * Restituisce il default del ruolo, con fallback esplicito.
 * @param {string} role
 * @returns {Object|null} null se il ruolo non ha un default definito.
 */
export function getDefaultForRole(role) {
    if (typeof role !== 'string') return null;
    const chiave = role.trim().toUpperCase();
    if (Object.prototype.hasOwnProperty.call(DEFAULTS_BY_ROLE, chiave)) {
        const d = DEFAULTS_BY_ROLE[chiave];
        /* Copia difensiva: il default non deve essere mutabile da chi lo legge. */
        return {
            version: d.version,
            order: d.order.slice(),
            widgets: Object.keys(d.widgets).reduce((acc, k) => {
                acc[k] = { enabled: d.widgets[k].enabled };
                return acc;
            }, {}),
        };
    }
    return null;
}

export default DEFAULTS_BY_ROLE;